// Hierarchical (cascading) scope options for the MIS report filters:
//   State -> City -> Branch -> Class / Section
// Given the full scope and the current selection, returns the options each
// dropdown should show so children are limited to their selected parents.
//
// Scope shape:
//   states:   [{ state_id, state_name }]
//   cities:   [{ id, city_name }]
//   branches: [{ id, branch_name, state_id, city_id }]
//   classes:  [{ id, class, branch_id }]
//   streams:  [{ id, stream_name, branch_id, class_id }]
//   sections: [{ id, section_name, branch_id, class_id }]

import { getEffectiveBranchIds } from "../../../utils/branchScope";

export const inSel = (arr, val) => !arr || !arr.length || arr.some((x) => String(x) === String(val));

export const toCsv = (arr) => (arr && arr.length ? arr.join(",") : "");

// Branch is chosen ONCE in the header (NavbarOne) and applies to every MIS tab, so it is
// no longer a per-tab filter. These are the branches the header has selected — the ROOT of
// the scope: State and City options are derived FROM them, then narrow back down.
// A header selection that doesn't overlap this institute falls back to everything the user
// is authorized for, so a tab never blanks out.
export function headerBranches(scope) {
  const all = (scope && scope.branches) || [];
  const sel = new Set(getEffectiveBranchIds().map(String));
  const scoped = sel.size ? all.filter((b) => sel.has(String(b.id))) : all;
  return scoped.length ? scoped : all;
}

// Effective branch ids = the header's branches, narrowed by the State/City selection.
export function effectiveBranchCsv(scope, sel) {
  const s = sel || {};
  const scoped = headerBranches(scope).filter(
    (b) => inSel(s.state, b.state_id) && inSel(s.city, b.city_id),
  );
  return toCsv(scoped.map((b) => b.id));
}

// Cascaded options for each dropdown, based on the current selection.
export function cascadeOptions(scope, sel) {
  scope = scope || {};
  sel = sel || {};
  // Root of the scope is the HEADER's branch selection — State/City are derived from it.
  const branchesAll = headerBranches(scope);

  // State options: only states the header's branches actually sit in.
  const stateIdsInScope = new Set(branchesAll.map((b) => String(b.state_id)));
  const states = (scope.states || []).filter((s) => stateIdsInScope.has(String(s.state_id)));

  // City options: cities of those branches, narrowed by the selected state(s).
  const cityIdsForState = new Set(
    branchesAll.filter((b) => inSel(sel.state, b.state_id)).map((b) => String(b.city_id))
  );
  const cities = (scope.cities || []).filter((c) => cityIdsForState.has(String(c.id)));

  // Branches feeding Class/Stream/Section = header branches ∩ selected state(s)+city(ies).
  const branches = branchesAll.filter((b) => inSel(sel.state, b.state_id) && inSel(sel.city, b.city_id));
  const branchIds = new Set(branches.map((b) => String(b.id)));

  // Effective branches for children = picked branches (∩ allowed) or all allowed.
  const pickedBranch = (sel.branch || []).filter((id) => branchIds.has(String(id)));
  const effBranchIds = new Set((pickedBranch.length ? pickedBranch : branches.map((b) => b.id)).map(String));

  // Class options: classes under the effective branches (deduped by class id).
  const classSeen = new Set();
  const classes = (scope.classes || []).filter((c) => {
    if (!effBranchIds.has(String(c.branch_id))) return false;
    if (classSeen.has(String(c.id))) return false;
    classSeen.add(String(c.id));
    return true;
  });
  const classIds = new Set(classes.map((c) => String(c.id)));
  const pickedClass = (sel.class || []).filter((id) => classIds.has(String(id)));
  const effClassIds = new Set((pickedClass.length ? pickedClass : classes.map((c) => c.id)).map(String));

  // Stream options: streams under the effective branches + classes (deduped by id).
  const streamSeen = new Set();
  const streams = (scope.streams || []).filter((st) => {
    if (!effBranchIds.has(String(st.branch_id)) || !effClassIds.has(String(st.class_id))) return false;
    if (streamSeen.has(String(st.id))) return false;
    streamSeen.add(String(st.id));
    return true;
  });

  // Section options: sections under the effective branches + classes.
  // Effective streams (picked or all-allowed) so sections cascade off the stream too.
  const streamIds = new Set(streams.map((st) => String(st.id)));
  const pickedStream = (sel.stream || []).filter((id) => streamIds.has(String(id)));
  const effStreamIds = new Set((pickedStream.length ? pickedStream : streams.map((st) => st.id)).map(String));
  const passStream = (v) => (v == null || String(v) === "" || String(v) === "0") ? true : effStreamIds.has(String(v));

  // Section options: sections under the effective branches + classes AND the selected
  // stream (a section with no stream always passes) — matches the MIS scope filter.
  const sections = (scope.sections || []).filter(
    (s) => effBranchIds.has(String(s.branch_id)) && effClassIds.has(String(s.class_id)) && passStream(s.stream_id)
  );

  return { states, cities, branches, classes, streams, sections };
}

// Drill-down enable map: each level unlocks only once its parent has a selection
// (Stream & Section both unlock on Class, since Stream is an optional refinement).
export function scopeEnabled(sel) {
  const s = sel || {};
  const has = (k) => (s[k] || []).length > 0;
  return { state: true, city: has("state"), branch: has("city"), class: has("branch"), stream: has("class"), section: has("class") };
}

// Apply a drill-down change: set `key` to `values`, clear everything downstream, and —
// if `values` is non-empty — auto-select ALL valid options for each deeper level (each
// re-derived against the updated selection). Returns the full next selection object.
export function applyCascade(scope, sel, key, values) {
  const order = ["state", "city", "branch", "class", "stream", "section"];
  const idx = order.indexOf(key);
  const next = { ...sel, [key]: values || [] };
  for (let i = idx + 1; i < order.length; i++) next[order[i]] = [];
  if ((values || []).length) {
    for (let i = idx + 1; i < order.length; i++) {
      const o = cascadeOptions(scope, next);
      const lvl = order[i];
      if (lvl === "city") next.city = o.cities.map((c) => c.id);
      else if (lvl === "branch") next.branch = o.branches.map((b) => b.id);
      else if (lvl === "class") next.class = [...new Set(o.classes.map((c) => c.id))];
      else if (lvl === "stream") next.stream = [...new Set(o.streams.map((s) => s.id))];
      else if (lvl === "section") next.section = o.sections.map((s) => s.id);
    }
  }
  return next;
}
